import React, { useEffect, useMemo } from "react";
import {
    CodeField as OriginalCodeField,
    CodeFieldProps as OriginalCodeFieldProps,
    Cursor,
    useBlurOnFulfill,
    useClearByFocusCell,
} from "react-native-confirmation-code-field";
import { v4 as uuidv4 } from "uuid";
import { Platform, StyleSheet, TextStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Text from "./Text";

type CodeFieldProps = {
    value: string;
    setValue: (v: string) => void;
    disabled?: boolean;
    cellCount: number;
    cellStyle?: TextStyle;
    autoFocus?: boolean;
    noBorders?: boolean;
} & Omit<OriginalCodeFieldProps, "renderCell">;

const CodeField: React.FC<CodeFieldProps> = ({
    cellCount,
    value,
    setValue,
    rootStyle,
    cellStyle,
    autoFocus = false,
    noBorders = false,
}) => {
    const ref = useBlurOnFulfill({ value, cellCount });
    const [props, getCellOnLayoutHandler] = useClearByFocusCell({
        value,
        setValue,
    });
    const styles = useCodeFieldStyle();

    const fieldId = useMemo(() => uuidv4(), []);

    // autoFocus prop doesn't natively work on react-native-confirmation-code-field on web
    useEffect(() => {
        if (Platform.OS === "web" && autoFocus) {
            document.getElementById(fieldId)?.focus?.();
        }

        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <OriginalCodeField
            ref={ref}
            {...props}
            id={fieldId}
            // Use `caretHidden={false}` when users can't paste a text value, because context menu doesn't appear
            value={value}
            onChangeText={setValue}
            cellCount={cellCount}
            rootStyle={[styles.codeFieldRoot, rootStyle]}
            keyboardType="number-pad"
            textContentType="oneTimeCode"
            autoFocus={autoFocus}
            textInputStyle={{ borderWidth: 2, borderColor: "red" }}
            renderCell={({ index, symbol, isFocused }) => (
                <Text
                    key={index}
                    allowFontScaling={false}
                    style={[styles.cell, cellStyle, isFocused && !noBorders && styles.focusCell]}
                    onLayout={getCellOnLayoutHandler(index)}
                >
                    {symbol || (isFocused ? <Cursor /> : null)}
                </Text>
            )}
        />
    );
};

const useCodeFieldStyle = () => {
    const { colors, gridSize, roundness } = useTheme();

    return useMemo(() => {
        return StyleSheet.create({
            codeFieldRoot: {
                ...(Platform.OS === "web" ? { justifyContent: "center" } : {}),
            },
            cell: {
                width: 48,
                height: 58,
                textAlignVertical: "center",
                alignSelf: "stretch",
                ...(Platform.OS === "web" ? { marginHorizontal: gridSize / 8 } : { flex: 0 }),
                fontSize: 30,
                lineHeight: 54,
                backgroundColor: colors.card,
                borderWidth: 1,
                borderColor: colors.backdrop,
                borderRadius: roundness,
                overflow: "hidden",
                textAlign: "center",
            },
            focusCell: {
                borderColor: colors.primary,
                borderWidth: 2,
            },
        });
    }, [gridSize, colors.card, colors.backdrop, colors.primary, roundness]);
};

export default CodeField;
